/* eslint-disable camelcase */
import Carousel from 'components/modules/Carousel';
import { FC } from 'react';
import Hero from 'components/modules/Hero';
import Title from 'components/modules/Title';
import Button from 'components/base/Button';
import { useQuery } from 'react-query';
import { useAppContext } from 'provider/AppProvider/AppProvider';
import { useDefaultColorValue } from 'services/queries/app';
import { getWelcomeData, getAppDataByUUID } from 'services/api/app';
import Loader from 'components/modules/Loader';
import Layout from 'components/template/Layout';
import { useFeaturedContext } from 'provider/FeaturedProvider/FeaturedProvider';
import { getUrlWithProtocol } from 'utils/getUrlWithProtocol';
import { useUserContext } from 'provider/UserProvider/UserProvider';
import InnerHTML from 'dangerously-set-html-content';
import { CarouselContainer, StyledWhatsNewContainer } from './Creator.styles';

const Creator: FC = () => {
  const { uuid, setup } = useAppContext();
  const { topFeatured, topFeaturedStatus } = useFeaturedContext();
  const { loggedInUser } = useUserContext();
  const colors = useDefaultColorValue();

  const { status, data } = useQuery(
    ['getWelcomeScreen', { uuid, token: loggedInUser?.id_token }],
    async () => {
      const response = await getWelcomeData(uuid, loggedInUser?.id_token);
      return response?.data?.[0];
    },
  );

  const useGetAppDataByUUID = (id: string) => {
    return useQuery(['getAppDataByUUID', { id }], async () => {
      return await getAppDataByUUID(id);
    });
  };

  /* eslint-disable-next-line @typescript-eslint/prefer-optional-chain */
  const enterpriseAppId = data?.enterprise_app?.id
    ? String(data?.enterprise_app?.id)
    : '';
  const { data: businessAppData } = useGetAppDataByUUID(enterpriseAppId);

  let description = data?.whats_new_description;

  if (data?.whats_new_web_description) {
    description = data?.whats_new_web_description;
  }

  const getAppName = () => {
    const username = businessAppData?.data?.username;
    return username || (data?.enterprise_app?.name ?? '');
  };

  const getRedirectUrl = () => {
    if (data?.enterprise && data?.enterprise_app?.name) {
      const appName: string = getAppName();
      return `${window.location.origin}/${appName}`;
    }

    if (
      !data?.enterprise &&
      data?.action_bar_url?.trim() &&
      data?.action_bar_url !== 'https://web.actionera.com/blank'
    ) {
      return getUrlWithProtocol(data?.action_bar_url);
    }

    return null;
  };

  const isButtonDisabled = () => {
    return !(
      data?.enterprise ||
      (data?.action_bar_url &&
        data?.action_bar_url !== 'https://web.actionera.com/blank')
    );
  };

  return (
    <Layout>
      <div className="h-full">
        {status === 'loading' ? (
          <Loader />
        ) : (
          <>
            <Hero
              headline={data?.web?.headline}
              imgSrc={data?.web?.image_url}
              videoSrc={data?.web?.video_url}
              featuredButtons={data?.featured_buttons}
              useFullscreen={data?.web?.stretch_image}
            />

            <StyledWhatsNewContainer
              bgColor={colors?.light}
              className="w-full px-8 lg:px-0"
            >
              <div className="container px-0 lg:px-20 w-full mx-auto flex flex-col py-8 justify-center items-center">
                <div className="text-center">
                  <Title fontSize="36px">{data?.whats_new_headline}</Title>
                </div>
                <InnerHTML
                  html={
                    description === null ||
                    description === '' ||
                    typeof description === 'undefined'
                      ? '<p></p>'
                      : description
                  }
                  className={`inner-html inner-html-custom text-black text-lg text-justify mt-4 mx-auto leading-6 w-full ttci-break-word ${
                    data?.whats_new_web_description
                      ? 'break-words'
                      : 'whitespace-pre-wrap'
                  }`}
                  style={{
                    fontFamily: setup?.fonts?.body,
                  }}
                />

                {data?.action_bar_text && (
                  <div className="w-full md:w-fit flex justify-center items-center mx-auto py-4 -px-4 md:p-4">
                    <a
                      className="w-full"
                      href={getRedirectUrl() ?? undefined}
                      target={isButtonDisabled() ? undefined : '_blank'}
                      rel={isButtonDisabled() ? undefined : 'noreferrer'}
                      onClick={(e) => {
                        if (isButtonDisabled()) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Button className="pt-[17px] pb-[17px]">
                        {data?.action_bar_text}
                      </Button>
                    </a>
                  </div>
                )}
              </div>
            </StyledWhatsNewContainer>

            {topFeaturedStatus !== 'loading' &&
            typeof topFeatured !== 'undefined' ? (
              <CarouselContainer bgColor={colors?.medium} className="w-full">
                <div className="container mx-auto px-6 sm:px-0">
                  <Carousel
                    bgColor={colors?.medium}
                    isMultiItemCarousel
                    items={topFeatured}
                  />
                </div>
              </CarouselContainer>
            ) : null}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Creator;
